import { NgFor, NgIf } from '@angular/common';
import { Component, inject} from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { StoredAlbum } from '@shared/types';
import { DatabaseService } from '../../services/database.service';
import { StorageService } from 'src/app/services/storage.service';
import { v4 as uuidv4 } from 'uuid';

@Component({
  selector: 'app-manage-albums',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgIf,
    NgFor,
    MatProgressSpinnerModule,
    MatIcon
  ],
  templateUrl: './manage-albums.component.html',
  styleUrl: './manage-albums.component.scss'
})
export class ManageAlbumsComponent {

  ready = false;

  albums: StoredAlbum[] = [];


  constructor(
    private db: DatabaseService,
    private dialog: MatDialog
  ) {
    this.loadAlbumList();
  }

  private async loadAlbumList() {
    this.albums = await this.db.getAlbums();
    this.ready = true;
  };

  editRecord(index: number) {
    const data = {
      mode: 'edit',
      index: index, 
      albums: this.albums
    };
    const dialogRef = this.dialog.open(ManageAlbumsDialog, {data});

    dialogRef.afterClosed().subscribe(result => {
      console.log('result', result);
      if ( result )
        this.albums = result;
    });
  }


  removeRecord(index: number) {
    const title = this.albums[index].title;
    const result = confirm(`Do you really want to remove album \'${title}\'?`);
    if ( result ) {
      this.albums.splice(index, 1);
      this.db.setAlbums(this.albums);
    }
  }

  public reorderRecord(index: number, direction: 'up' | 'down') {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const length = this.albums.length
    if (newIndex >= 0 && newIndex < length) {
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      const movedAlbum = this.albums.splice(index, 1)[0];
      this.albums.splice(newIndex, 0, movedAlbum);
    }
    this.db.setAlbums(this.albums);
  }


  addRecord(index: number) {
    const data = {
      mode: 'add',
      index: index, 
      albums: this.albums
    };
    const dialogRef = this.dialog.open(ManageAlbumsDialog, {data});
    dialogRef.afterClosed().subscribe(
      (albums: StoredAlbum[] | null) => {
      if ( albums ) {
        this.albums = albums;
      }
    });
  }

}





// MANAGE-ALBUMS DIALOG
@Component({
  selector: 'manage-albums-dialog',
  templateUrl: 'manage-albums-dialog.html',
  styleUrl: './manage-albums.component.scss',
  standalone: true,
  imports: [
    NgIf,
    ReactiveFormsModule,
    MatProgressSpinnerModule
  ],
})
export class ManageAlbumsDialog {

  readonly dialogRef = inject(MatDialogRef);
  readonly data = inject<{
    mode: 'add' | 'edit',
    index: number,
    albums: StoredAlbum[]
  }>(MAT_DIALOG_DATA);

  editForm: FormGroup;
  uploading = false;
  loadingAlbum = false;

  imageSrc: string = '';
  file: File | null = null; 

  constructor(
    private db: DatabaseService,
    private storage: StorageService,
    public formBuilder: FormBuilder
  ) {
    //console.log('data', this.data);
    this.editForm = this.formBuilder.group({
      id: [''],
      imgUrl: ['', Validators.required],
      title: ['', Validators.required],
      author: ['', Validators.required],
      label: ['', Validators.required],
      year: [''],
      description: ['']
    });

    if ( this.data.mode === 'edit' ) {
      const album = this.data.albums[this.data.index];
      this.editForm.setValue({
        id: album.id,
        imgUrl: album.imgUrl,
        title: album.title,
        author: album.author,
        label: album.label || '',
        year: album.year || '',
        description: album.description || ''
      });
      this.loadingAlbum = true;
    }
  }

  preventSubmit(event: KeyboardEvent): void {
    if (event instanceof KeyboardEvent && event.key === 'Enter') {
      event.preventDefault(); // Impealbume il comportamento di default
    }
  }

  onFileSelected(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target.files && target.files.length > 0) {
      this.file = target.files[0];
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.imageSrc = e.target.result; // Imposta l'immagine selezionata come nuova sorgente
        this.loadingAlbum = true;
      };
      reader.readAsDataURL(this.file); // Legge il file come Data URL
    }
  }

  onImageLoad(): void {
    this.loadingAlbum = false;
  }

  public async saveRecord() {
    if ( this.data.mode === 'edit') {
      const updatedAlbum: StoredAlbum = this.editForm.value;
      this.data.albums[this.data.index] = updatedAlbum;
    } 
    else { // mode === 'add'
      if (this.file) {
        try {
          this.uploading = true;
          const url = await this.storage.uploadImageAndGetUrl('albums',this.file);
          this.editForm.value.imgUrl = url;
          this.editForm.value.id = uuidv4();
          const newAlbum: StoredAlbum = this.editForm.value;
          this.data.albums.splice(this.data.index, 0, newAlbum);
        } catch (error) {
          console.error('Errore durante il caricamento:', error);
        } finally {
          this.uploading = false;
        }
      }
    }
    await this.db.setAlbums(this.data.albums)
      .then( () => {
        this.dialogRef.close(this.data.albums);
      })
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

}
